import { Renderer, Camera, Geometry, Program, Mesh } from 'ogl';

const vertexShader = `
  attribute vec3 position;
  attribute vec4 random;
  attribute vec3 color;

  uniform mat4 modelMatrix;
  uniform mat4 viewMatrix;
  uniform mat4 projectionMatrix;
  uniform float uTime;
  uniform float uSpread;
  uniform float uBaseSize;

  varying vec4 vRandom;
  varying vec3 vColor;

  void main() {
    vRandom = random;
    vColor = color;

    vec3 pos = position * uSpread;

    vec4 mPos = modelMatrix * vec4(pos, 1.0);
    float t = uTime;

    float horizontalMovement = (sin(t * 0.0008 + random.x * 6.28) + 1.0) * 0.5;
    float verticalOscillation = sin(t * 0.0005 + random.y * 6.28) * 0.3;

    mPos.x = (horizontalMovement - 0.5) * 2.0 * 15.0;
    mPos.y += verticalOscillation;
    mPos.z = random.z * 2.0 - 1.0;

    vec4 mvPos = viewMatrix * mPos;
    gl_PointSize = (uBaseSize * (0.5 + random.w * 0.5)) / length(mvPos.xyz);
    gl_Position = projectionMatrix * mvPos;
  }
`;

const fragmentShader = `
#ifdef GL_FRAGMENT_PRECISION_HIGH
  precision highp float;
#else
  precision mediump float;
#endif

  uniform float uAlpha;
  varying vec4 vRandom;
  varying vec3 vColor;

  void main() {
    vec2 uv = gl_PointCoord.xy;
    float d = length(uv - vec2(0.5));

    if(d > 0.5) {
      discard;
    }

    float circle = smoothstep(0.5, 0.3, d);
    gl_FragColor = vec4(vColor, circle * uAlpha);
  }
`;

function initParticlesBackground(container, options = {}) {
    if (!container) {
        console.warn('Particles background: Container element not found');
        return null;
    }

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        return null;
    }

    const {
        particleCount = 100,
        particleSpread = 10,
        speed = 0.1,
        particleColors = ['#ffffff', '#ffffff'],
        particleBaseSize = 50,
        alphaParticles = 0.6,
        cameraDistance = 20,
        blur = 0,
        pixelRatio = Math.min(window.devicePixelRatio || 1, 1.5)
    } = options;

    let renderer;
    try {
        renderer = new Renderer({
            dpr: pixelRatio,
            depth: false,
            alpha: true,
            antialias: true
        });
    } catch (error) {
        console.warn('Particles background: WebGL not supported or failed to initialize', error);
        return null;
    }

    const gl = renderer.gl;

    if (!gl) {
        console.warn('Particles background: WebGL context not available');
        return null;
    }

    if (blur > 0) {
        gl.canvas.style.filter = `blur(${blur}px)`;
    }

    gl.canvas.style.position = 'absolute';
    gl.canvas.style.top = '0';
    gl.canvas.style.left = '0';
    gl.canvas.style.pointerEvents = 'none';
    gl.canvas.style.backgroundColor = 'transparent';
    gl.canvas.style.visibility = 'hidden';

    container.appendChild(gl.canvas);
    gl.clearColor(0, 0, 0, 0);

    const camera = new Camera(gl, { fov: 15 });
    camera.position.set(0, 0, cameraDistance);

    const applyResize = () => {
        const { clientWidth, clientHeight } = container;
        renderer.setSize(clientWidth, clientHeight);
        gl.canvas.style.width = clientWidth + 'px';
        gl.canvas.style.height = clientHeight + 'px';
        gl.canvas.style.visibility = 'visible';
        const aspect = (gl.drawingBufferWidth && gl.drawingBufferHeight)
            ? gl.drawingBufferWidth / gl.drawingBufferHeight
            : gl.canvas.width / gl.canvas.height;
        camera.perspective({ aspect });
    };

    let resizeTimer;
    const resize = () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(applyResize, 100);
    };

    window.addEventListener('resize', resize, false);
    applyResize();

    const count = particleCount;
    const positions = new Float32Array(count * 3);
    const randoms = new Float32Array(count * 4);
    const colors = new Float32Array(count * 3);

    const hexToRgb = hex => {
        hex = hex.replace(/^#/, '');
        if (hex.length === 3) {
            hex = hex.split('').map(c => c + c).join('');
        }
        const int = parseInt(hex, 16);
        const r = ((int >> 16) & 255) / 255;
        const g = ((int >> 8) & 255) / 255;
        const b = (int & 255) / 255;
        return [r, g, b];
    };

    const rgbColors = particleColors.map(hexToRgb);

    for (let i = 0; i < count; i++) {
        let x, y, z, len;
        do {
            x = Math.random() * 2 - 1;
            y = Math.random() * 2 - 1;
            z = Math.random() * 2 - 1;
            len = x * x + y * y + z * z;
        } while (len > 1 || len === 0);

        const r = Math.cbrt(Math.random());
        positions.set([x * r, y * r, z * r], i * 3);
        randoms.set([Math.random(), Math.random(), Math.random(), Math.random()], i * 4);

        const colorIndex = Math.floor(Math.random() * rgbColors.length);
        colors.set(rgbColors[colorIndex], i * 3);
    }

    const geometry = new Geometry(gl, {
        position: { size: 3, data: positions },
        random: { size: 4, data: randoms },
        color: { size: 3, data: colors }
    });

    let program;
    try {
        program = new Program(gl, {
            vertex: vertexShader,
            fragment: fragmentShader,
            uniforms: {
                uTime: { value: 0 },
                uSpread: { value: particleSpread },
                uBaseSize: { value: particleBaseSize * pixelRatio },
                uAlpha: { value: alphaParticles }
            },
            transparent: true,
            depthTest: false
        });
    } catch (error) {
        console.warn('Particles background: Failed to create shader program', error);
        if (container.contains(gl.canvas)) {
            container.removeChild(gl.canvas);
        }
        return null;
    }

    const particles = new Mesh(gl, { mode: gl.POINTS, geometry, program });

    let animationFrameId;
    let lastTime = performance.now();
    let elapsed = 0;
    let hasShutDown = false;
    let isVisible = true;

    const observer = new IntersectionObserver(
        (entries) => { isVisible = entries[0].isIntersecting; },
        { threshold: 0 }
    );
    observer.observe(container);

    const gracefulShutdown = () => {
        if (hasShutDown) {
            return;
        }
        hasShutDown = true;
        observer.disconnect();
        try { cancelAnimationFrame(animationFrameId); } catch (e) {}
        try { window.removeEventListener('resize', resize); } catch (e) {}
        try {
            gl.canvas.style.transition = 'opacity 50ms linear';
            gl.canvas.style.opacity = '0';
        } catch (e) {}
        setTimeout(() => {
            try { if (container.contains(gl.canvas)) container.removeChild(gl.canvas); } catch (e) {}
            try { gl.getExtension('WEBGL_lose_context')?.loseContext(); } catch (e) {}
        }, 140);
    };

    const onBeforeUnload = () => gracefulShutdown();
    window.addEventListener('beforeunload', onBeforeUnload, { once: true });

    const update = t => {
        animationFrameId = requestAnimationFrame(update);
        if (!isVisible) {
            return;
        }
        const delta = t - lastTime;
        lastTime = t;
        elapsed += delta * speed;

        program.uniforms.uTime.value = elapsed;

        renderer.render({ scene: particles, camera });
    };

    animationFrameId = requestAnimationFrame(update);

    return () => {
        window.removeEventListener('beforeunload', onBeforeUnload);
        gracefulShutdown();
    };
}

document.addEventListener('DOMContentLoaded', () => {
    const containers = document.querySelectorAll('.particles-bg-layer');

    containers.forEach(container => {
        const isMobile = window.innerWidth < 768;
        const baseParticleCount = parseInt(container.dataset.particleCount) || 100;
        const particleCount = isMobile ? Math.floor(baseParticleCount * 0.5) : baseParticleCount;
        const particleSpread = parseFloat(container.dataset.particleSpread) || 10;
        const speed = parseFloat(container.dataset.speed) || 0.1;
        const particleBaseSize = parseFloat(container.dataset.particleBaseSize) || 50;
        const alphaParticles = parseFloat(container.dataset.alphaParticles) || 0.6;
        const cameraDistance = parseFloat(container.dataset.cameraDistance) || 20;
        const blur = parseFloat(container.dataset.blur) || 0;
        const particleColorStr = container.dataset.particleColors || '#ffffff';
        const particleColors = particleColorStr.split(',').map(c => c.trim());

        initParticlesBackground(container, {
            particleCount,
            particleSpread,
            speed,
            particleColors,
            particleBaseSize,
            alphaParticles,
            cameraDistance,
            blur
        });
    });
});
